import { ReactElement, useCallback, useMemo } from 'react';

import { ModalConfirmButton } from '@dreryk/react-components';
import { getPreviousWorkweekEnd, isWeekendDate } from '@dreryk/react-calendar';

import { TimetableActions } from '../../shared/TimetableActions/TimetableActions';
import { calendarTranslationKeys } from '../../../../translations';
import {
   TimetableSelectedDate,
   TimetableSelectedDateSetter,
   VisitsVisibilityPreferences,
} from '../../shared/TimetableActions/TimetableActions.types';
import { useWeekendsVisibilityPreference } from '../SpecialistTimetable.hooks';
import {
   useCancelledVisibilityPreferenceState,
   useScheduleOnlyVisibilityPreferenceState,
} from '../../shared/preferences/preferences.storage';

import { SpecialistTimetablePreferencesForm } from './form/SpecialistTimetablePreferencesForm';

type SpecialistTimetableActionsProps = {
   selectedDate: TimetableSelectedDate;
   setSelectedDate: TimetableSelectedDateSetter;
};

export const SpecialistTimetableActions = (props: SpecialistTimetableActionsProps): ReactElement => {
   const [preferCancelledVisitsVisible, setCancelledVisibilityPreference] = useCancelledVisibilityPreferenceState();
   const [, setScheduleOnlyVisibilityPreference] = useScheduleOnlyVisibilityPreferenceState();
   const [, setWeekendsVisibilityPreference] = useWeekendsVisibilityPreference();

   const visitsVisibilityPreferenceInitialValues = useMemo(
      () => ({
         preferCancelledVisitsVisible: Boolean(preferCancelledVisitsVisible),
      }),
      [preferCancelledVisitsVisible]
   );

   const handleVisitsVisibilityPreferencesChange = useCallback(
      (visitsVisibilityPreferences: VisitsVisibilityPreferences) => {
         setCancelledVisibilityPreference(visitsVisibilityPreferences.preferCancelledVisitsVisible);
      },
      [setCancelledVisibilityPreference]
   );

   return (
      <TimetableActions
         onVisitsVisibilityPreferencesChange={handleVisitsVisibilityPreferencesChange}
         visitsVisibilityPreferencesInitialValues={visitsVisibilityPreferenceInitialValues}
      >
         {({ closeModal }) => (
            <SpecialistTimetablePreferencesForm>
               {({ handleSubmit }) => (
                  <ModalConfirmButton
                     label={calendarTranslationKeys.save}
                     onClick={handleSubmit(({ preferWeekendsVisible, preferScheduleOnlyVisible }) => {
                        setScheduleOnlyVisibilityPreference(preferScheduleOnlyVisible);
                        setWeekendsVisibilityPreference(preferWeekendsVisible);
                        if (!preferWeekendsVisible && isWeekendDate(props.selectedDate)) {
                           props.setSelectedDate(getPreviousWorkweekEnd(props.selectedDate));
                        }
                        closeModal();
                     })}
                  />
               )}
            </SpecialistTimetablePreferencesForm>
         )}
      </TimetableActions>
   );
};
